<template>
  <div>
    <RoleBasedHeader :userRole="userRole" />
    <div class="container cursor-pointer">
      <div class="mt-4 text-end pe-4">
        <a @click="$router.go(-1)" class="mb-4">
          <i class="bi bi-chevron-left"></i> Go Back
        </a>
      </div>

    </div>

    <div class="container mt-5" style="margin-bottom: 7rem">
      <div class="row">
        <div class="col-md-8">
          <div class="fm-margin">
            <job-intake-chat v-if="useChat && !skipChat" :is-logged-in="!!loggedInUser" @skip="skipChat = true" @confirm="onIntakeConfirm"/>
            <div v-if="!useChat || skipChat">
            <!-- Step 0: Select trade — hidden once the chat has collected the job
                 (the chat -> register flow skips the manual wizard entirely). -->
            <div class="mb-5" v-if="!pendingIntakeJob">
              <h2 class="mb-4">Post a {{ selectedTradeName }} job</h2>
              <p class="text-muted mb-4">Get responses from Trade Link's vetted tradespeople near you</p>
              <h5 class="fw-bold mb-2">What trades would you like to have done?</h5>
              <select v-model="selectedTrade" class="form-control" @change="resetForms" :disabled="isLoading">
                <option value="" v-if="isLoading">Please wait...</option>
                <option disabled value="">Please select</option>
                <option v-for="(trade, i) in trades" :key="i" :value="trade.id">
                  {{ trade.name }}
                </option>
              </select>
              <div class="button-container mt-4" v-if="!questions.length && !questionLoader">
                <button class="btn primry-btn-2 d-inline-block text-light big-button"
                        :disabled="!selectedTrade"
                        @click.prevent="loadFirstStep">
                  Next
                </button>
              </div>
            </div>

            <div v-if="questionLoader">
              <div class="text-center">
                <b-spinner small></b-spinner>
                <p class="text-muted">Loading questions...</p>
              </div>
            </div>

            <div v-else>
              <form class="dynamic-questions" v-if="!pendingIntakeJob" @submit.prevent="nextQuestion">
                <div class="question-section" v-for="(question, i) in questions" :key="i">
                  <h5 class="fw-bold mb-2" :id="'question-' + i" :ref="'question-' + i">{{ question.formLabel }}</h5>
                  <div v-if="question.options && question.options.length">
                    <div v-for="(option, index) in question.options" :key="index">
                      <!-- Radio Option -->
                      <label class="option-box" v-if="option.type === 'StandardChoiceOption'">
                        <input type="radio"
                               class="me-2"
                               :id="'option-' + question.id + '-' + index"
                             :name="'question-' + question.id"
                             :value="option.formLabel"
                             :required="question.required"
                             v-model="question.answers"
                             @change="onOptionSelected(option,i)">
                      <span :class="getTimeframeClass(option.formLabel)">{{ option.formLabel }}</span>
                      <div v-if="isOtherSelected(question,option)">
                        <input type="text"
                               class="form-control mt-2"
                               required
                               v-model="question.other_answer"
                               placeholder="Please specify"/>
                      </div>
                    </label>
                    <!-- Checkbox Option -->
                    <label class="option-box" v-else-if="option.type === 'StandardCheckOption'">
                      <input type="checkbox"
                             class="me-2"
                             :id="'checkbox-' + question.id + '-' + index"
                             :value="option.formLabel"
                             v-model="question.answers">
                      <span>{{ option.formLabel }}</span>
                      <div v-if="isCheckboxOtherSelected(option, i)">
                        <input type="text"
                               class="form-control mt-2"
                               v-model="question.answers"
                               placeholder="Please specify"/>
                      </div>
                    </label>
                    <!-- Textarea Option -->
                    <div v-else-if="option.type === 'StandardTextAreaOption'">
                      <p class="text-muted">{{ option.formLabel }}</p>
                      <textarea :id="'textarea-' + question.id + '-' + index"
                                class="form-control mt-2"
                                v-model="question.answers"
                                :required="question.required"
                                @input="onOptionSelected(option, i)"
                                placeholder="Type your response here"></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div class="button-container mt-4" v-if="!lastQuestion && questions.length">
                <button class="btn btn-outline-primary-1 d-inline-block text-dark big-button"
                        @click.prevent="prevQuestion" type="button">
                  Back
                </button>
                <button class="btn primry-btn-2 d-inline-block text-light big-button">
                  Next
                </button>
              </div>
            </form>

            <div v-if="lastQuestion" class="mt-5">
              <form v-if="showHeadline" @submit.prevent="showHeadlineNextBtn">
                <h5 class="fw-bold mb-2" ref="showHeadline">Give your job a headline</h5>
                <p class="text-muted">More tradespeople express interest in jobs that have a descriptive name.</p>
                <input type="text" class="form-control" v-model="headline"
                       @click="showSection('showHeadline')" required>

                <div class="button-container mt-4" v-if="navButtonPosition ==='showHeadline'">
                  <button class="btn btn-outline-primary-1 d-inline-block text-dark big-button" type="button"
                          @click.prevent="showHeadlineBackBtn">
                    Back
                  </button>
                  <button class="btn primry-btn-2 d-inline-block text-light big-button" type="submit">
                    Next
                  </button>
                </div>

              </form>
              <form v-if="showAddPhoto" ref="showAddPhoto" class="mt-5" @submit.prevent="showAddPhotoNextBtn">
                <h5 class="fw-bold mb-2">Would you like to add any photos or plans? (Optional)</h5>
                <p class="text-muted">Photos give tradespeople more context and details to understand the job and
                  provide a more accurate quote.</p>
                <label class="option-box">
                  <input type="radio"
                         @change="showSection('showAddPhoto')"
                         class="me-2"
                         id="yes"
                         required
                         name="photo"
                         value="Yes"
                         v-model="photo">
                  <span>Yes</span>
                </label>

                <div v-if="photo ==='Yes'">
                  <p class="text-muted">Add up to 5 files up to 15 MB each</p>
                  <vue-dropzone
                      id="certificateDropzone"
                      ref="dropzone"
                      :use-custom-slot="true"
                      :options="dropzoneOptions"
                      @vdropzone-max-files-exceeded="dropzoneMaxFileExceeded"
                  >
                    <div class="dz-message needsclick">
                      <i class="bi bi-upload h1 text-muted"></i>
                      <h5>Select files, drag and drop, or take a photo with your camera.</h5>
                    </div>
                  </vue-dropzone>
                </div>

                <label class="option-box">
                  <input type="radio"
                         @change="showSection('showAddPhoto')"
                         class="me-2"
                         id="no"
                         required
                         name="photo"
                         value="No"
                         v-model="photo">
                  <span>No, maybe later</span>
                </label>

                <div class="button-container mt-4" v-if="navButtonPosition ==='showAddPhoto'">
                  <button class="btn btn-outline-primary-1 d-inline-block text-dark big-button"
                          @click.prevent="showAddPhotoBackBtn" type="button">
                    Back
                  </button>
                  <button class="btn primry-btn-2 d-inline-block text-light big-button" type="submit">
                    Next
                  </button>
                </div>

              </form>
              <form v-if="showLocationView" ref="showLocationView" class="mt-5"
                    @submit.prevent="showLocationViewNextBtn">
                <h5 class="fw-bold mb-2">Location for the job</h5>
                <p class="text-muted">To find {{ selectedTradeName }}s near you we need to know where the job is.</p>
                <!-- Places API (New) PlaceAutocompleteElement is mounted here at runtime.
                     Replaces the legacy <GmapAutocomplete> / google.maps.places.Autocomplete,
                     which Google blocks for API keys created after 2025-03-01. -->
                <div
                    ref="autocompleteEl"
                    id="citySearch"
                    class="places-autocomplete-wrap"
                    style="flex: 1;"
                    @click="showSection('showLocationView'); locationError =''"
                ></div>
                <div v-if="locationError" class="text-danger mt-2"><i class="bi bi-info-circle"/> {{ locationError }}
                </div>
                <div class="button-container mt-5" v-if="navButtonPosition ==='showLocationView'">
                  <button class="btn btn-outline-primary-1 d-inline-block text-dark big-button" type="button"
                          @click.prevent="showLocationViewBackBtn">
                    Back
                  </button>
                  <button class="btn primry-btn-2 d-inline-block text-light big-button" type="submit">
                    Next
                  </button>
                </div>
              </form>
              <form v-if="showAuthView" ref="showAuthView" style="margin-top: 4rem"
                    @submit.prevent="verifyHomeOwnerEmail">
                <h5 class="fw-bold mb-2">Get responses from tradespeople near you</h5>
                <p class="text-muted">We will only share your contact details with tradespeople you choose to talk
                  to.</p>
                <label for="email">Email</label>
                <input type="text" @click="showSection('showAuthView');" id="email" class="form-control" v-model="email" required>
                <div class="text-danger mt-2" v-if="verificationError"><i
                    class="bi bi-info-circle"/>{{ verificationError }}
                </div>

                <div class="button-container mt-4" v-if="navButtonPosition ==='showAuthView'">
                  <button class="btn btn-outline-primary-1 d-inline-block text-dark big-button" type="button"
                          @click.prevent="showAuthViewBackBtn">
                    Back
                  </button>
                  <button class="btn primry-btn-2 d-inline-block text-light big-button" type="submit"
                          :disabled="submitLoading">
                    <b-spinner small v-if="submitLoading"></b-spinner>
                    {{ submitLoading ? 'Next..' : 'Next' }}
                  </button>
                </div>
              </form>

              <form v-if="showLoginView" ref="showLoginView" style="margin-top: 4rem" @submit.prevent="loginHomeOwner">
                <div class="row">
                  <div class="col-md-6">
                    <h5 class="fw-bold mb-2">Log in</h5>
                    <p class="text-muted">{{ email }} <span class="text-decoration-underline cursor-pointer"
                                                            @click="goToAuthView">Change</span></p>
                    <label for="password">Password</label>
                    <input type="password" id="password" class="form-control" v-model="password" required @click="showSection('showLoginView');">

                    <div class="button-container mt-4" v-if="navButtonPosition ==='showLoginView'">
                      <button class="btn btn-block btn-primary" type="submit"
                              :disabled="submitLoading">
                        <b-spinner small v-if="submitLoading"></b-spinner>
                        {{ submitLoading ? 'Next..' : 'Login' }}
                      </button>
                    </div>
                  </div>
                </div>

              </form>

              <form v-if="showRegisterView" ref="showRegisterView" style="margin-top: 4rem"
                    @submit.prevent="registerHomeOwner">
                <div class="row">
                  <div class="col-md-12">
                    <h5 class="fw-bold mb-2">Create an account to track your job</h5>
                    <p class="text-muted">We will only share your contact details with tradespeople you choose to talk
                      to.</p>

                    <!-- Intake mode: summary of the chat-collected job + email field
                         (the chat -> register flow skips the separate email step). -->
                    <div v-if="pendingIntakeJob" class="alert alert-light border mb-3">
                      Almost done — create your free account to post your
                      <strong>{{ pendingIntakeJob.trade }}</strong> job<span v-if="pendingIntakeJob.parish_name"> in {{ pendingIntakeJob.parish_name }}</span>.
                      We've saved everything you told us, so you won't need to re-enter it.
                    </div>
                    <div v-if="pendingIntakeJob" class="form-group mb-3">
                      <label for="intakeEmail">Email</label>
                      <input type="email" @click="showSection('showRegisterView')" id="intakeEmail" class="form-control mt-1" v-model="email" required>
                    </div>

                    <div class="form-group">
                      <label for="name">Name</label>
                      <input type="text" @click="showSection('showRegisterView')" class="form-control mt-1" id="name" v-model="name" required>
                    </div>
                    <div class="form-group mt-3">
                      <label for="phone">Phone Number</label>
                      <input type="text" @click="showSection('showRegisterView')" class="form-control mt-1" id="phone" v-model="phoneNumber" required>
                    </div>
                    <div class="form-group mt-3">
                      <label for="password">Password</label>
                      <input type="password" @click="showSection('showRegisterView')" class="form-control mt-1" id="password" v-model="password" required>
                    </div>

                    <div class="form-group form-check mt-3">
                      <input type="checkbox" class="form-check-input" id="exampleCheck1" v-model="marketing_consent" @click="showSection('showRegisterView')">
                      <label class="form-check-label text-muted cursor-pointer font-14" for="exampleCheck1">I would like
                        to receive marketing
                        communications about Trade Link's services by email, SMS and/or phone and understand that I can
                        unsubscribe at any time.</label>
                    </div>

                    <div class="button-container mt-4" v-if="navButtonPosition ==='showRegisterView'">
                      <button class="btn btn-outline-primary-1 d-inline-block text-dark big-button" type="button"
                              @click.prevent="registerBackBtn" v-if="!pendingIntakeJob">
                        Back
                      </button>
                      <button class="btn btn-primary big-button" type="submit"
                              :disabled="submitLoading">
                        <b-spinner small v-if="submitLoading"></b-spinner>
                        {{ submitLoading ? 'Posting…' : (pendingIntakeJob ? 'Create account & post my job' : 'Next') }}
                      </button>
                    </div>
                    <p v-if="pendingIntakeJob" class="mt-3 text-muted">
                      Already have an account?
                      <span class="text-decoration-underline cursor-pointer" @click="switchToIntakeLogin">Log in instead</span>
                    </p>
                  </div>
                </div>

              </form>
            </div>
            </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuestionRenderer from '../../../components/QuestionRenderer.vue';
import JobIntakeChat from '@/components/JobIntakeChat.vue';
import {userService} from "@/apis/user.service";
import vue2Dropzone from "vue2-dropzone";
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import appConfig from "../../../../app.config.json";
import store from "@/store/store";
import RoleBasedHeader from "@/views/base-layout/roleBasedHeader";
import {getRecaptchaToken} from "@/utils/recaptcha";

export default {
  name: "PostAJob",
  page: {
    title: "Post a Job | " + appConfig.name,
    meta: [{name: "description", content: appConfig.pageDescriptions.postJob}]
  },
  components: {
    RoleBasedHeader,
    QuestionRenderer,
    JobIntakeChat,
    vueDropzone: vue2Dropzone,
  },

  data() {
    return {
      place: null,
      user: this.$store.getters.GET_USER_INFO,
      useChat: process.env.VUE_APP_ENABLE_JOB_INTAKE === 'true',
      skipChat: false,
      pendingIntakeJob: null,
      trades: [],
      selectedTradeName: '',
      selectedTrade: '',
      rootQuestions: [],
      questions: [],
      currentQuestionIndex: 0,
      answers: {},
      selectedOption: null,
      nextButton: true,
      isLoading: false,
      questionLoader: false,
      headline: '',
      lastQuestion: false,
      showHeadline: true,
      showAddPhoto: false,
      showLocationView: false,
      showAuthView: false,
      submitLoading: false,
      showLoginView: false,
      showRegisterView: false,
      navButtonPosition: '',
      photo: '',
      dropzoneOptions: {
        url: '#',
        maxFilesize: 20.0,
        addRemoveLinks: true,
        autoProcessQueue: false,
        maxFiles: 5,
        acceptedFiles: 'image/*'
      },
      cityName: '',
      parishName: '',
      email: '',
      password: '',
      marketing_consent: '',
      name: '',
      phoneNumber: '',
      verificationError: '',
      locationError: '',
      latlng: null
    };
  },

  computed: {
    loggedInUser() {
      return this.$store.getters.GET_USER_INFO;
    },
    userRole() {
      const loggedUser = store.getters.GET_USER_INFO;
      if(!loggedUser){
        return '';
      }
      return loggedUser?.roles?.[0] || '';
    },
  },
  watch: {
    // The location <form> is v-if-gated, so its container is (re)created each
    // time this step is shown. Mount a fresh PlaceAutocompleteElement then.
    showLocationView(visible) {
      if (visible) {
        this.$nextTick(() => this.initLocationAutocomplete());
      }
    },
  },
  methods: {
    getTimeframeClass(label) {
      const normalized = label.toString().toLowerCase();
      if (normalized.includes('immediately')) return 'text-danger';
      if (normalized.includes('1–2 weeks')) return 'text-warning';
      if (normalized.includes('within a month')) return 'text-primary-1';
      if (normalized.includes('flexible') || normalized.includes('not urgent')) return 'text-secondary';
      return 'bg-light';
    },
    async initLocationAutocomplete() {
      // Places API (New): build a PlaceAutocompleteElement and mount it into the
      // location field container. Replaces the legacy places.Autocomplete widget,
      // which is unavailable for API keys created after 2025-03-01.
      await this.$gmapApiPromiseLazy();
      let PlaceAutocompleteElement;
      if (google.maps.importLibrary) {
        ({PlaceAutocompleteElement} = await google.maps.importLibrary('places'));
      } else {
        PlaceAutocompleteElement = google.maps.places && google.maps.places.PlaceAutocompleteElement;
      }
      const container = this.$refs.autocompleteEl;
      if (!container) return;
      if (!PlaceAutocompleteElement) {
        console.error('PlaceAutocompleteElement unavailable — check the Maps JS version (v=weekly) and that "Places API (New)" is enabled on the key.');
        return;
      }
      container.innerHTML = '';

      const pac = new PlaceAutocompleteElement({
        includedRegionCodes: ['jm'],
        // Approximates the legacy types:['(cities)'] filter.
        includedPrimaryTypes: ['locality', 'sublocality', 'administrative_area_level_3', 'postal_town'],
      });
      pac.setAttribute('placeholder', 'Eg. Albion');
      pac.style.width = '100%';
      container.appendChild(pac);

      // Handle both the current (gmp-select → placePrediction) and older
      // (gmp-placeselect → place) event shapes so this survives a version bump.
      const onSelect = async (event) => {
        this.locationError = '';
        let place = event.place;
        if (!place && event.placePrediction && event.placePrediction.toPlace) {
          place = event.placePrediction.toPlace();
        }
        if (!place) return;
        await place.fetchFields({fields: ['location']});
        const loc = place.location;
        if (!loc) return;
        this.latlng = {lat: loc.lat(), lng: loc.lng()};
        // Unchanged downstream: reverseGeocode fills cityName + parishName.
        this.reverseGeocode(this.latlng.lat, this.latlng.lng);
      };
      pac.addEventListener('gmp-select', onSelect);
      pac.addEventListener('gmp-placeselect', onSelect);
    },
    dropzoneMaxFileExceeded(file) {
      this.$refs.dropzone.removeFile(file);
      // alert('You can only upload one file at a time.');
    },
    resetForms() {
      this.loadFirstStep();
      this.selectedTradeName = '';
      this.questions = [];
      this.currentQuestionIndex = 0;
      this.lastQuestion = false;
      this.showHeadline = false;
      this.showAddPhoto = false;
      this.showLocationView = false;
      this.showAuthView = false;
      this.showLoginView = false;
      this.showRegisterView = false;
    },
    getTrades() {
      this.isLoading = true;
      userService.postJobTrades().then((res) => {
        this.isLoading = false;
        this.trades = res.extra;
        const routerQuery = this.$route.query.category;
        if (routerQuery) {
          const tradeObj = this.trades.find(t => t.name.toLowerCase() === routerQuery.toLowerCase());
          if (tradeObj) {
            this.selectedTrade = tradeObj.id;
            this.selectedTradeName = tradeObj.name;
          }
        }
      });
    },
    loadFirstStep() {
      const tradeObj = this.trades.find(t => t.id === this.selectedTrade);
      if (tradeObj) this.selectedTradeName = tradeObj.name;
      this.fetchQuestions();
    },
    fetchQuestions() {
      this.questionLoader = true
      userService.getTradeQuestion(this.selectedTrade).then((res) => {
        this.questionLoader = false
        this.questions = Array.isArray(res.extra) ? res.extra : [res.extra];
        setTimeout(() => {
          const el = this.$refs['question-' + this.currentQuestionIndex];
          if (el && el[0]) {
            el[0].scrollIntoView({behavior: 'smooth', block: 'start'});
          }
        }, 200);
      });
    },
    isOtherSelected(question,option) {
      return option.formLabel.toLowerCase() === 'other' &&
          question.answers?.toString().toLowerCase() === 'other';
    },
    isCheckboxOtherSelected(option) {
      return option.formLabel.toLowerCase() === 'other' &&
          this.answers[this.question.id]?.includes(option.formLabel);
    },
    onOptionSelected(option, questionIndex) {
      this.lastQuestion = false;
      this.currentQuestionIndex = questionIndex
      if (this.questions.length - 1 !== questionIndex) {
        this.questions.splice(questionIndex + 1);
      }
      this.selectedOption = option;
    },
    nextQuestion() {
      this.lastQuestion = false;
      const questionOptions = this.questions[this.currentQuestionIndex].options
      if (questionOptions.length < 2 && !questionOptions[0].next) {
        this.lastQuestion = true;
        this.showSection('showHeadline')
        this.scrollToView('showHeadline')
        return;
      }
      if (this.selectedOption && this.selectedOption.next) {
        const nextId = this.selectedOption.next.id;
        const alreadyExists = this.questions.some(q => q.id === nextId);
        if (!alreadyExists) {
          this.selectedOption.next.answers = null
          const nxtQuestion = {
            ...this.selectedOption.next,
            next_question_id: nextId,
          };
          this.questions.push(nxtQuestion);
          this.currentQuestionIndex++;

          setTimeout(() => {
            const el = this.$refs['question-' + this.currentQuestionIndex];
            if (el && el[0]) {
              el[0].scrollIntoView({behavior: 'smooth', block: 'start'});
            }
          }, 200);
        }
      }

      if (this.selectedOption && !this.selectedOption.next) {
        this.lastQuestion = true;
        this.showSection('showHeadline')
        this.scrollToView('showHeadline')
      }
    },
    prevQuestion() {
      this.currentQuestionIndex--;
      this.questions.pop();
      this.selectedOption = this.questions[this.currentQuestionIndex]?.options?.find(
          option => option.formLabel === this.questions[this.currentQuestionIndex].answers)
    },

    scrollToView(reference) {
      this.$nextTick(() => {
        const el = this.$refs[reference];
        if (!el) return
        setTimeout(() => {
          el.scrollIntoView({behavior: 'smooth', block: 'start'});
        }, 200);
      });
    },
    showHeadlineNextBtn() {
      this.showAddPhoto = true;
      this.navButtonPosition = 'showAddPhoto';
      this.scrollToView('showAddPhoto');
    },
    showHeadlineBackBtn() {
      this.lastQuestion = false;
    },
    showAddPhotoNextBtn() {
      this.showLocationView = true;
      this.navButtonPosition = 'showLocationView';
      this.scrollToView('showLocationView');
    },

    showAddPhotoBackBtn() {
      this.showHeadline = true;
      this.navButtonPosition = 'showHeadline';
      this.showAddPhoto = false;
      this.scrollToView('showHeadline');
    },
    showLocationViewBackBtn() {
      this.showAddPhoto = true;
      this.navButtonPosition = 'showAddPhoto';
      this.showLocationView = false;
      this.scrollToView('showAddPhoto');
    },
    registerBackBtn() {
      this.showRegisterView = false;
      this.showAuthView = true;
    },

    showSection(section) {
      this[section] = true;
      this.navButtonPosition = section;

      const sections = [
        'showHeadline',
        'showAddPhoto',
        'showLocationView',
        'showAuthView',
        'showLoginView',
        'showRegisterView'
      ];


      const currentIndex = sections.indexOf(section);
      for (let i = currentIndex + 1; i < sections.length; i++) {
        this[sections[i]] = false;
      }
    },

    showLocationViewNextBtn() {
      if (!this.latlng) {
        this.locationError = 'Please select a location for the job.'
        return;
      }
      // check if user is logged in as homesperson then navigate to congrats page
      if (this.loggedInUser) {
        this.submitForm()
        return;
      }
      this.showAuthView = true;
      this.navButtonPosition = 'showAuthView';
      this.scrollToView('showAuthView');
    },
    showAuthViewBackBtn() {
      if (this.pendingIntakeJob) {
        // Intake mode: the location step was never used, so cancel the pending
        // post and return to a fresh chat instead of an empty location view.
        this.pendingIntakeJob = null;
        this.showAuthView = false;
        this.showLoginView = false;
        this.showRegisterView = false;
        this.lastQuestion = false;
        this.skipChat = false;
        return;
      }
      this.showLocationView = true;
      this.navButtonPosition = 'showLocationView';
      this.showAuthView = false;
      this.scrollToView('showLocationView');
    },

    verifyHomeOwnerEmail() {
      this.submitLoading = true;
      this.verificationError = '';
      const payload = {
        email: this.email
      }
      userService.verifyHomeOwner(payload).then((res) => {
        this.submitLoading = false;
        const {status, message} = res;
        if (status) {
          this.showLoginView = true;
          this.navButtonPosition = 'showLoginView';
          this.scrollToView('showLoginView')
        } else {
          if (message === 'User not found.') {
            this.showRegisterView = true;
            this.navButtonPosition = 'showRegisterView';
            this.scrollToView('showRegisterView')
          } else if (message === 'User is not a homeowner.') {
            this.verificationError = 'Use a different email address to post your job'
          } else {
            this.verificationError = message
          }
        }
      });

    },

    loginHomeOwner() {
      this.submitLoading = true;
      this.$store.dispatch("login", {
        email: this.email,
        password: this.password,
      }).then((res) => {
        this.submitLoading = false;
        const {status, message, extra} = res;
        if (!status) {
          this.$store.dispatch('error', {message: message, showSwal: true});
          return;
        }
        if (this.pendingIntakeJob) { this.saveIntakeJob() } else { this.submitForm() }
      });
    },
    registerHomeOwner() {
      this.submitLoading = true;
      const payload = {
        email: this.email,
        password: this.password,
        name: this.name,
        phone: this.phoneNumber,
        marketing_consent: this.marketing_consent
      };
      userService.registerHomeOwner(payload).then((res) => {
        this.submitLoading = false;
        const {status, message, extra} = res;
        if (!status) {
          this.$store.dispatch('error', {message: message, showSwal: true});
          return;
        }
        this.$store.dispatch('updateUserInfo', extra);
        if (this.pendingIntakeJob) { this.saveIntakeJob() } else { this.submitForm() }
      });
    },

    goToAuthView() {
      this.showAuthView = true;
      this.showLoginView = false;
    },
    async submitForm() {
      this.submitLoading = true;
      const formData = new FormData();

      formData.append('trade_id', this.selectedTrade);
      formData.append('headline', this.headline);
      formData.append('city_name', this.cityName);
      formData.append('parish_name', this.parishName);
      formData.append('latitude', this.latlng.lat);
      formData.append('longitude', this.latlng.lng);
      formData.append('email', this.email);
      formData.append('user_id', this.$store.getters.GET_USER_INFO.id);

      // Append questions as JSON string
      formData.append('questions', JSON.stringify(this.questions));

      // Append photos if 'Yes'
      if (this.photo === 'Yes') {
        const files = this.$refs.dropzone.getAcceptedFiles();
        files.forEach((file, index) => {
          formData.append(`photo[${index}]`, file);
        });
      }

      const recaptcha_token = await getRecaptchaToken('post_job');
      formData.append('recaptcha_token', recaptcha_token || '');

      userService.postJob(formData).then((res) => {
        this.submitLoading = false;
        this.trades = res.extra;
        this.$router.push('/tradesperson-recommendation/' + res.extra.project_id);
      })
    },

    // AI job-intake: the chat widget emits confirm with the finished job.
    // Logged-in homeowners save immediately; anonymous visitors are routed
    // through the page's existing email -> login/register step (reused, not duplicated).
    onIntakeConfirm({job}) {
      this.pendingIntakeJob = job;
      if (this.loggedInUser) {
        this.saveIntakeJob();
        return;
      }
      // Anonymous: hide the chat + the whole manual wizard and go straight to a
      // clean register step. The job rides silently in pendingIntakeJob and posts
      // automatically once they register (or log in) — no re-entering anything.
      this.skipChat = true;
      this.lastQuestion = true;
      this.showHeadline = false;
      this.showAddPhoto = false;
      this.showLocationView = false;
      this.showAuthView = false;
      this.showLoginView = false;
      this.showRegisterView = true;
      this.navButtonPosition = 'showRegisterView';
      this.$nextTick(() => this.scrollToView('showRegisterView'));
    },
    saveIntakeJob() {
      this.submitLoading = true;
      userService.postIntakeJob(this.pendingIntakeJob).then((res) => {
        this.submitLoading = false;
        if (res && res.project_id) {
          this.$router.push('/tradesperson-recommendation/' + res.project_id);
        } else {
          this.$store.dispatch('error', {message: 'Could not post the job. Please try the form.', showSwal: true});
          // Safety net: drop intake mode so the normal manual form re-appears.
          this.pendingIntakeJob = null;
          this.skipChat = true;
        }
      });
    },
    switchToIntakeLogin() {
      // Existing-account users tap "Log in instead": reuse the login form, which
      // carries this.email; loginHomeOwner already auto-posts pendingIntakeJob.
      this.showRegisterView = false;
      this.showLoginView = true;
      this.navButtonPosition = 'showLoginView';
      this.$nextTick(() => this.scrollToView('showLoginView'));
    },

    reverseGeocode(lat, lng) {
      this.$gmapApiPromiseLazy().then(() => {
        const geocoder = new google.maps.Geocoder();
        const latlng = {lat, lng};

        geocoder.geocode({location: latlng}, (results, status) => {
          if (status === 'OK' && results[0]) {
            const addressComponents = results[0].address_components;

            const cityComponent = addressComponents.find(comp =>
                comp.types.includes('locality')
            );
            const parishComponent = addressComponents.find(comp =>
                comp.types.includes('administrative_area_level_1')
            );

            this.cityName = cityComponent ? cityComponent.long_name : '';
            this.parishName = parishComponent ? parishComponent.long_name : '';

          } else {
            console.log('Reverse geocoding failed:', status);
          }
        });
      });
    }
  },
  created() {
    this.getTrades();
  },
  mounted() {
    $('.sidebar-button').on("click", function () {
      $('.main-menu').addClass('show-menu');
    });

    $('.menu-close-btn').on("click", function () {
      $('.main-menu').removeClass('show-menu');
    });
  }

};
</script>

<style scoped>
.question-section {
  padding-bottom: 20px;
}

.option-box {
  border: 1px solid #ddd;
  padding: 15px;
  margin: 10px 0;
  border-radius: 10px;
  cursor: pointer;
  display: block;
}

.option-box:hover {
  background: #f9f9f9;
}

.option-box input[type="radio"],
.option-box input[type="checkbox"] {
  margin-right: 10px;
}

select {
  height: 3rem !important;
  appearance: auto !important;
}

input[type="text"], input[type="password"] {
  height: 3rem !important;
}

.form-control {
  font-size: 17px !important;
}

.button-container {
  display: flex;
  gap: 10px;
}
</style>
